/**
 * Pure helpers for the AREA-OPS pages. No React here, so everything is
 * unit-tested in lib.test.ts.
 */

// ----------------------------------------------------------------- variables

export type VarType = 'text' | 'number' | 'boolean' | 'json'

export interface VarRow {
  /** Stable row identity for React keys (not sent to the API). */
  id: string
  key: string
  type: VarType
  /** Editable text. Strings are raw; other types are their JSON text. */
  text: string
}

export interface VarRowErrors {
  key?: string
  value?: string
}

/** Keys must be plain identifiers so `app.vars.<key>` works in Handlebars and CEL. */
export const VAR_KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/

let rowSeq = 0
export function newRowId(): string {
  rowSeq += 1
  return `var-${rowSeq}`
}

export function typeOfValue(v: unknown): VarType {
  if (typeof v === 'string') return 'text'
  if (typeof v === 'number') return 'number'
  if (typeof v === 'boolean') return 'boolean'
  return 'json'
}

export function textOfValue(v: unknown, type: VarType = typeOfValue(v)): string {
  if (type === 'text') return typeof v === 'string' ? v : JSON.stringify(v)
  if (type === 'json') return JSON.stringify(v, null, 2)
  return String(v)
}

export function varsToRows(vars: Record<string, unknown> | null | undefined): VarRow[] {
  return Object.entries(vars ?? {}).map(([key, value]) => {
    const type = typeOfValue(value)
    return { id: newRowId(), key, type, text: textOfValue(value, type) }
  })
}

/** Parse one row's text by its type. Returns `{ value }` or `{ error }`. */
export function parseVarValue(row: Pick<VarRow, 'type' | 'text'>): {
  value?: unknown
  error?: string
} {
  const t = row.text.trim()
  switch (row.type) {
    case 'text':
      return { value: row.text }
    case 'number': {
      if (t === '' || !Number.isFinite(Number(t))) return { error: 'Enter a number' }
      return { value: Number(t) }
    }
    case 'boolean':
      if (t === 'true' || t === 'false') return { value: t === 'true' }
      return { error: 'Choose true or false' }
    case 'json':
      if (t === '') return { error: 'Enter a JSON value' }
      try {
        return { value: JSON.parse(t) as unknown }
      } catch (e) {
        return { error: `Invalid JSON: ${e instanceof Error ? e.message : String(e)}` }
      }
  }
}

/**
 * Convert editor rows to the variables object. Rows with an empty key and
 * empty value are ignored (a fresh blank row). Errors are keyed by row id.
 */
export function rowsToVars(rows: VarRow[]): {
  vars: Record<string, unknown>
  errors: Record<string, VarRowErrors>
  valid: boolean
} {
  const vars: Record<string, unknown> = {}
  const errors: Record<string, VarRowErrors> = {}
  const seen = new Set<string>()
  for (const row of rows) {
    const key = row.key.trim()
    if (!key && !row.text.trim()) continue
    const e: VarRowErrors = {}
    if (!key) e.key = 'Enter a name'
    else if (!VAR_KEY_PATTERN.test(key))
      e.key = 'Use letters, digits, and underscores, starting with a letter'
    else if (seen.has(key)) e.key = 'This name is already used'
    seen.add(key)
    const parsed = parseVarValue(row)
    if (parsed.error) e.value = parsed.error
    if (e.key || e.value) errors[row.id] = e
    else vars[key] = parsed.value
  }
  return { vars, errors, valid: Object.keys(errors).length === 0 }
}

/** Change a row's type, converting its text where that makes sense. */
export function convertRowType(row: VarRow, type: VarType): VarRow {
  if (row.type === type) return row
  const parsed = parseVarValue(row)
  let text = row.text
  if (parsed.error === undefined) {
    if (type === 'text')
      text = typeof parsed.value === 'string' ? parsed.value : JSON.stringify(parsed.value)
    else if (type === 'json') text = JSON.stringify(parsed.value, null, 2)
    else if (type === 'boolean')
      text = parsed.value === true || row.text.trim() === 'true' ? 'true' : 'false'
    else if (type === 'number')
      text = Number.isFinite(Number(row.text.trim())) ? row.text.trim() : ''
  }
  return { ...row, type, text }
}

// --------------------------------------------------------------------- JSON

/** JSON with object keys sorted at every level, for stable diffs. */
export function stableJson(value: unknown): string {
  if (value === undefined) return ''
  return JSON.stringify(sortKeys(value), null, 2)
}

function sortKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortKeys)
  if (v && typeof v === 'object') {
    return Object.fromEntries(
      Object.keys(v as Record<string, unknown>)
        .sort()
        .map((k) => [k, sortKeys((v as Record<string, unknown>)[k])]),
    )
  }
  return v
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/**
 * Dotted paths whose values differ between `before` and `after`. Objects are
 * walked; arrays and scalars compare whole. Capped at `limit` paths.
 */
export function changedPaths(before: unknown, after: unknown, limit = 50): string[] {
  const out: string[] = []
  const walk = (a: unknown, b: unknown, path: string) => {
    if (out.length >= limit) return
    if (isPlainObject(a) && isPlainObject(b)) {
      const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])].sort()
      for (const k of keys) walk(a[k], b[k], path ? `${path}.${k}` : k)
      return
    }
    if (JSON.stringify(sortKeys(a)) !== JSON.stringify(sortKeys(b))) out.push(path || '(value)')
  }
  walk(before ?? null, after ?? null, '')
  return out
}

// ------------------------------------------------------------------ audit

const VERBS: Record<string, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  revoke: 'Revoked',
  publish: 'Published',
  activate: 'Activated a version of',
  draft_save: 'Saved the draft of',
  draft_discard: 'Discarded the draft of',
  rules_replace: 'Changed the rules of',
  demo: 'Seeded',
  suspend: 'Suspended',
  resume: 'Reactivated',
  rotate_secret: 'Rotated the secret of',
  test_send: 'Sent a test of',
  resend: 'Resent',
  cancel: 'Cancelled',
  live_enabled: 'Turned on Live sending for',
  live_disabled: 'Turned off Live sending for',
  apply: 'Applied',
  dismiss: 'Dismissed',
}

const NOUNS: Record<string, string> = {
  application: 'application',
  api_key: 'API key',
  workload_identity: 'workload identity',
  sender: 'sender',
  template: 'template',
  partial: 'partial',
  event_type: 'event type',
  suppression: 'suppression',
  webhook: 'webhook',
  message: 'message',
  proposal: 'proposal',
  seed: 'demo data',
}

/** Entity types the audit filter offers (the API accepts any string). */
export const AUDIT_ENTITY_TYPES = [
  'application',
  'api_key',
  'workload_identity',
  'sender',
  'template',
  'partial',
  'event_type',
  'webhook',
  'suppression',
  'message',
  'proposal',
] as const

export function entityLabel(entityType: string): string {
  return NOUNS[entityType] ?? entityType.replace(/_/g, ' ')
}

/** "template.publish" -> "Published template". Unknown verbs fall back to the raw action. */
export function describeAction(action: string, entityType?: string): string {
  const dot = action.indexOf('.')
  if (dot < 0) return action
  const subject = action.slice(0, dot)
  const verb = action.slice(dot + 1)
  const v = VERBS[verb]
  if (!v) return action
  if (subject === 'seed') return 'Seeded demo data'
  return `${v} ${entityLabel(entityType ?? subject)}`
}

// ---------------------------------------------------------- time windows

export type TimeWindow = '1h' | '24h' | '7d' | '30d' | '90d'

const WINDOW_MS: Record<TimeWindow, number> = {
  '1h': 3600_000,
  '24h': 24 * 3600_000,
  '7d': 7 * 24 * 3600_000,
  '30d': 30 * 24 * 3600_000,
  '90d': 90 * 24 * 3600_000,
}

export function isTimeWindow(v: unknown): v is TimeWindow {
  return typeof v === 'string' && v in WINDOW_MS
}

/** Start of a trailing window, rounded down to the minute so query keys stay stable. */
export function windowStart(w: TimeWindow, now = Date.now()): string {
  const t = now - WINDOW_MS[w]
  return new Date(Math.floor(t / 60_000) * 60_000).toISOString()
}

// ------------------------------------------------------------- key expiry

export type ExpiryPreset = 'never' | '30d' | '90d' | '1y' | 'custom'

/** ISO timestamp for a preset, or for a custom `YYYY-MM-DD` date (end of that day, local time). */
export function expiryFromPreset(
  preset: ExpiryPreset,
  customDate: string | undefined,
  now = Date.now(),
): string | null {
  const day = 24 * 3600_000
  switch (preset) {
    case 'never':
      return null
    case '30d':
      return new Date(now + 30 * day).toISOString()
    case '90d':
      return new Date(now + 90 * day).toISOString()
    case '1y':
      return new Date(now + 365 * day).toISOString()
    case 'custom': {
      if (!customDate || !/^\d{4}-\d{2}-\d{2}$/.test(customDate)) return null
      const [y, m, d] = customDate.split('-').map(Number) as [number, number, number]
      return new Date(y, m - 1, d, 23, 59, 59).toISOString()
    }
  }
}

// ---------------------------------------------------------------- emails

/** Mirrors the server's pragmatic `is_valid_email`. */
export function isValidEmail(email: string): boolean {
  if (email.length > 254 || /[\s\p{Cc}]/u.test(email)) return false
  const at = email.indexOf('@')
  if (at <= 0) return false
  const local = email.slice(0, at)
  const domain = email.slice(at + 1)
  return (
    local.length <= 64 &&
    !domain.includes('@') &&
    domain.includes('.') &&
    !domain.startsWith('.') &&
    !domain.endsWith('.') &&
    !domain.includes('..') &&
    !/[<>,;"]/.test(local)
  )
}

/** An email address, or an `@domain` allow-list entry. */
export function isValidEmailOrDomain(entry: string): boolean {
  return entry.startsWith('@') ? isValidEmail(`x${entry}`) : isValidEmail(entry)
}

/** Split pasted text (commas, whitespace, semicolons, newlines) into trimmed entries. */
export function splitEntries(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

// ------------------------------------------------------------ quick start

/** `curl` for POST /v1/events with the app's first event type and sample. */
export function buildQuickStartCurl(opts: {
  baseUrl: string
  eventKey: string
  payload: unknown
  apiKey?: string
}): string {
  const base = opts.baseUrl.replace(/\/+$/, '') || 'http://localhost:5000'
  // Indent the body under `-d` so the JSON reads as part of the command.
  const body = JSON.stringify({ event: opts.eventKey, payload: opts.payload ?? {} }, null, 2)
    .split('\n')
    .join('\n  ')
  // Single-quote the body for the shell; escape any single quotes in it.
  const quoted = `'${body.replace(/'/g, `'\\''`)}'`
  return [
    `curl -X POST ${base}/v1/events \\`,
    `  -H "Authorization: Bearer ${opts.apiKey ?? 'ms_test_XXXXXXXX_your_api_key'}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -H "Idempotency-Key: $(uuidgen)" \\`,
    `  -d ${quoted}`,
  ].join('\n')
}

/** The GUID format Entra uses for application (client) IDs. */
export const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
